/**
 * Calculate total invoice amount including IVA and subtracting IRPF.
 * @param {number} base - The base amount before taxes.
 * @param {number} ivaPercent - The IVA percentage (e.g., 21, 10, 4, 0).
 * @param {number} irpfPercent - The IRPF percentage (e.g., 15, 7, 0).
 * @returns {Object} An object containing the base, ivaAmount, irpfAmount, and total.
 */
export const calculateTaxes = (base, ivaPercent = 21, irpfPercent = 0) => {
  const baseAmount = parseFloat(base) || 0;
  const ivaAmount = baseAmount * (parseFloat(ivaPercent) / 100);
  const irpfAmount = baseAmount * (parseFloat(irpfPercent) / 100);
  
  const total = baseAmount + ivaAmount - irpfAmount;

  return {
    base: baseAmount,
    ivaAmount,
    irpfAmount,
    total
  };
};

// Round to 2 decimals (euro cents), half-up.
export const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

// IBI (Impost sobre les Begudes Ensucrades Envasades) is a Catalan regional tax —
// it only applies to sales within Catalonia. Use the client's province to decide.
const CATALAN_PROVINCES = ['barcelona', 'girona', 'lleida', 'tarragona', 'gerona', 'lérida', 'lerida'];
export const isClientInCatalunya = (client) => {
  const prov = (client?.province || '').toLowerCase().trim();
  return prov.length > 0 && CATALAN_PROVINCES.some((cp) => prov.includes(cp));
};

// Recargo de equivalencia surcharge rate per IVA rate.
export const recargoRateFor = (r) => ({ 21: 5.2, 10: 1.4, 5: 0.62, 4: 0.5 }[Number(r)] || 0);

// Document totals computed the AEAT way: the cuota (IVA) and the recargo de
// equivalencia are worked out PER IVA rate and rounded to the cent on each rate,
// then those rounded figures are added up. This is how a Spanish factura is
// calculated by hand — so the printed breakdown rows always add up exactly to
// the totals. (Summing the raw per-line amounts and rounding only at the end can
// drift a cent, e.g. recargo 0,12 + 0,87 + 3,07 = 4,06, not 4,05.)
// `byRate` is the output of groupLinesByTaxRate.
export const sumByRateTotals = (byRate) => {
  let subtotal = 0, ivaTotal = 0, recargoTotal = 0, ibiTotal = 0;
  (byRate || []).forEach((r) => {
    // Subtotal = Base Imponible = pure product cost. IVA is charged on this alone;
    // the IBAE is surfaced separately and added on top (see docs/IBAE_TAX_FORMULA.md).
    subtotal += round2(r.productBase ?? r.base);
    ivaTotal += round2(r.iva);
    recargoTotal += round2(r.recargo);
    ibiTotal += round2(r.ibi);
  });
  return {
    subtotal: round2(subtotal),
    ivaTotal: round2(ivaTotal),
    recargoTotal: round2(recargoTotal),
    ibiTotal: round2(ibiTotal),
  };
};

// IBEE (Catalan tax on packaged sugary drinks) tariff per product tier, in €/L.
// 0 = not applicable, 1 = 0,08 €/L (5–8 g/100 ml), 2 = 0,12 €/L (>8 g/100 ml).
// NOTE: these are the original 2017 rates, kept BY CLIENT CHOICE. The current
// legal rates (since 1-Jul-2020, Llei 5/2020) are 0,10/0,15 — do NOT "correct"
// them without asking the client. Base = litres delivered (qty × L).
export const ibiRateFor = (tier) => ({ 1: 0.08, 2: 0.12 }[Number(tier)] || 0);

// IBI amount for a single document line: quantity × litres-per-unit × €/L tariff.
// It's a fixed per-litre tax, so the line discount (a price reduction) does not
// reduce it. Accepts either the editable-grid shape ({ volume_l, ibi }) or the
// stored DB row shape (same column names). IBI is a product-level tax (sugary
// drinks): it applies whenever the product carries an IBI tier + volume,
// independent of the client. The optional `applyIbi` flag, when explicitly set
// to false, lets a document opt out entirely; otherwise the line decides.
export const lineIbiAmount = (line, applyIbi = true) => {
  if (applyIbi === false) return 0;
  const qty = Number(line.quantity) || 0;
  const volume = Number(line.volume_l ?? line.volumeL) || 0;
  return qty * volume * ibiRateFor(line.ibi);
};

// Whether any line in a document carries IBI — used to decide if the IBI column
// should render in the printed tax breakdown, regardless of the client flag.
export const documentHasIbi = (lines) =>
  (lines || []).some((line) => lineIbiAmount(line) > 0);

// Prices are entered VAT-inclusive (the final price the customer pays) but stored
// as the net base (sin IVA), which is what the invoice/estimate totals build on.
// These convert between the two given an IVA rate.
export const grossToNet = (gross, rate) =>
  (parseFloat(gross) || 0) / (1 + (parseFloat(rate) || 0) / 100);
export const netToGross = (net, rate) =>
  (parseFloat(net) || 0) * (1 + (parseFloat(rate) || 0) / 100);

/**
 * A section-title line (lib/documentLines.js): editor shape `lineType`, stored
 * shape `line_type`. It has no quantity, price or tax and must never take part
 * in any total. Defined here (not imported) so this module stays dependency-free.
 */
export const isTitleRow = (line) =>
  !!line && (line.lineType === 'title' || line.line_type === 'title');

/**
 * Groups document lines by IVA rate for the printed tax breakdown table
 * (Tipo IVA / Base Imponible / Cuota / Recargo de equivalencia).
 * Accepts either the editable-grid line shape ({ unitPrice, taxRate }) or the
 * stored DB row shape ({ unit_price, tax_rate }).
 */
export const groupLinesByTaxRate = (lines, { recargoEquiv = false, applyIbi = true } = {}) => {
  const map = {};
  (lines || []).forEach((line) => {
    // A section title ("Mano de obra") carries no amount: skip it entirely so
    // it never opens a phantom 0 % row in the printed breakdown.
    if (isTitleRow(line)) return;
    const qty = Number(line.quantity) || 0;
    const price = Number(line.unitPrice ?? line.unit_price) || 0;
    const discount = Number(line.discount) || 0;
    const rate = Number(line.taxRate ?? line.tax_rate) || 0;
    // Prefer the stored per-line net total (the amount actually billed) over
    // qty × unit_price. unit_price is persisted at 2 decimals, so on large
    // quantities re-deriving the base drifts cents from the issued figures — e.g.
    // 100 × 0,56 = 56,00 vs the billed 56,20 — which made the printed per-rate
    // rows disagree with the totals row. The stored `total` is already net, signed
    // and discount-applied; editable-grid lines have no `total`, so they fall back
    // to the live computation.
    const productBase = (line.total != null && line.total !== '')
      ? Number(line.total)
      : qty * price * (1 - discount / 100);
    // IBAE (Catalan sugary-drinks tax) applies ONLY when the document opted in
    // (applyIbi) — lineIbiAmount returns 0 otherwise. THE canonical rule (see
    // docs/IBAE_TAX_FORMULA.md — do NOT change): the IBAE is a fixed per-litre
    // charge added ON TOP of the invoice; it is NOT part of the IVA taxable base.
    // IVA and recargo are charged on the product base ALONE, and the IBAE is then
    // added once with no tax on it → Base Imponible excludes IBAE, and the tax
    // table columns (Base + Cuota + Recargo + IBAE) sum exactly to the total.
    const ibi = lineIbiAmount(line, applyIbi);
    const iva = productBase * rate / 100;
    const recargo = recargoEquiv ? productBase * recargoRateFor(rate) / 100 : 0;
    if (!map[rate]) map[rate] = { rate, base: 0, productBase: 0, iva: 0, recargo: 0, ibi: 0 };
    map[rate].base += productBase;   // Base Imponible = product base (IBAE excluded)
    map[rate].productBase += productBase;
    map[rate].iva += iva;
    map[rate].recargo += recargo;
    map[rate].ibi += ibi;
  });
  return Object.values(map).sort((a, b) => a.rate - b.rate);
};

/* ─── Retail (PVP-driven) ticket totals — TPV toggle "tpv_gross_totals" ───
 * The canonical formula above is NET-driven: base = Σ cent-rounded net lines,
 * IVA = round(base × rate). That is exact for invoices, but a shelf label
 * carries a PVP (gross), and the customer expects TOTAL = Σ PVP × qty. The
 * two disagree whenever the IVA rounding goes the other way from the PVP
 * rounding — e.g. PVP 2,10 @ 21 %: net 1,7355 → base 1,74 → IVA 0,37 →
 * total 2,11 (ticket 2026/4064, riwayat). No 2-decimal base can even reach
 * 2,10 at 21 % (1,73 → 2,09; 1,74 → 2,11), so this needs its own formula:
 *
 *   line gross  = round2(qty × PVP × (1 − dto))
 *   per rate r  : G_r = Σ line gross;  base_r = round2(G_r / (1 + r));
 *                 iva_r = round2(G_r − base_r)            (IVA incluido method)
 *   TOTAL       = Σ G_r  (+ IBAE on top, untaxed, as always)
 *
 * Per-account opt-in (default OFF): accounts that don't enable it keep the
 * canonical formula untouched. Lines carry the TPV cart shape
 * ({ unitPrice = gross, quantity, taxRate, discount, ibi, volume_l }). */
export const retailTotals = (lines, { applyIbi = true } = {}) => {
  const map = {};
  let ibiTotal = 0;
  const lineGross = (lines || []).map((l) => {
    // Title rows keep their slot (0) so the per-line arrays stay aligned with
    // `lines`, but open no rate row and add nothing.
    if (isTitleRow(l)) return 0;
    const qty = Number(l.quantity) || 0;
    const rate = Number(l.taxRate ?? l.tax_rate) || 0;
    const gross = round2(qty * (Number(l.unitPrice) || 0) * (1 - (Number(l.discount) || 0) / 100));
    if (!map[rate]) map[rate] = { rate, gross: 0, base: 0, iva: 0 };
    map[rate].gross = round2(map[rate].gross + gross);
    ibiTotal += lineIbiAmount(l, applyIbi);
    return gross;
  });
  let base = 0, iva = 0, grand = 0;
  const rows = Object.values(map).sort((a, b) => a.rate - b.rate).map((r) => {
    const b = round2(r.gross / (1 + r.rate / 100));
    const i = round2(r.gross - b);
    base = round2(base + b); iva = round2(iva + i); grand = round2(grand + r.gross);
    return { ...r, base: b, iva: i };
  });
  ibiTotal = round2(ibiTotal);
  return { base, iva, ibi: ibiTotal, grand: round2(grand + ibiTotal), rows, lineGross };
};

/* Net line totals to STORE for a retail ticket: each line's gross de-grossed
 * and cent-rounded, then the per-rate residual (≤ a few cents on long tickets)
 * put on that rate's largest line so Σ stored line totals == base_r exactly —
 * the printed tax table (rebuilt from the lines) then shows the same base the
 * header carries. Returns one net total per line, same order as `lines`. */
export const retailLineNetTotals = (lines, totals) => {
  const nets = (lines || []).map((l, i) =>
    isTitleRow(l) ? 0 : round2(totals.lineGross[i] / (1 + (Number(l.taxRate ?? l.tax_rate) || 0) / 100)));
  for (const r of totals.rows) {
    const idx = [];
    (lines || []).forEach((l, i) => { if (!isTitleRow(l) && (Number(l.taxRate ?? l.tax_rate) || 0) === r.rate) idx.push(i); });
    if (!idx.length) continue;
    const sum = round2(idx.reduce((s, i) => s + nets[i], 0));
    const diff = round2(r.base - sum);
    if (diff !== 0) {
      const big = idx.reduce((a, b) => (nets[b] > nets[a] ? b : a), idx[0]);
      nets[big] = round2(nets[big] + diff);
    }
  }
  return nets;
};

/* ─── Document (factura/presupuesto) totals the PVP-driven way ───────────────
 * Same formula as retailTotals above, shaped for the invoice/estimate editors:
 * it returns the per-rate rows those editors already feed to the preview, the
 * PDF and the stored header columns, plus the net line totals to persist.
 *
 * WHY a document needs it. The catalogue stores the NET price (the typed
 * VAT-inclusive price ÷ (1 + rate), Products.jsx), so a document rebuilds the
 * gross by multiplying back — and the canonical net-driven formula then rounds
 * the cuota as round2(base × rate). Neither step can always land back on the
 * price that was typed: at 18 % no 2-decimal base reaches 280.000,00 at all
 * (237.288,13 → 279.999,99; 237.288,14 → 280.000,01), so an account selling a
 * 280,00 article 1.000 at a time could never print 280.000,00. Deriving the
 * cuota as GROSS − BASE instead makes qty × price exact by construction.
 *
 * Lines come in the editor shape ({ unitPrice = gross, quantity (already
 * signed), taxRate, discount, ibi, volume_l }). Per-account opt-in
 * (document_design.doc_gross_totals); every account that leaves it OFF keeps
 * the canonical formula, untouched. */
export const documentGrossTotals = (lines, { recargoEquiv = false, applyIbi = true } = {}) => {
  const rows = lines || [];
  const rt = retailTotals(rows, { applyIbi });
  // Per-rate IBAE, so the printed breakdown keeps its own IBAE column (the
  // retail rows only carry a document-wide figure).
  const ibiByRate = {};
  rows.forEach((l) => {
    if (isTitleRow(l)) return;
    const rate = Number(l.taxRate ?? l.tax_rate) || 0;
    ibiByRate[rate] = round2((ibiByRate[rate] || 0) + lineIbiAmount(l, applyIbi));
  });
  const byRate = rt.rows.map((r) => ({
    rate: r.rate,
    base: r.base,
    productBase: r.base,
    iva: r.iva,
    // Recargo de equivalencia still rides on the base, rounded per rate.
    recargo: recargoEquiv ? round2(r.base * recargoRateFor(r.rate) / 100) : 0,
    ibi: ibiByRate[r.rate] || 0,
  }));
  return {
    byRate,
    // One net total per line, same order as `lines`, summing per rate to
    // exactly that rate's base — so a reopened/printed document rebuilds the
    // same breakdown from the stored rows.
    lineNets: retailLineNetTotals(rows, rt),
    subtotal: rt.base,
    ivaTotal: rt.iva,
    ibiTotal: rt.ibi,
    recargoTotal: round2(byRate.reduce((s, r) => s + r.recargo, 0)),
  };
};

/* The printed tax table is rebuilt from the lines (IVA = round(base × rate)),
 * while the TOTAL comes from the stored header. For a retail-rounded ticket
 * the header IVA is G_r − base_r, which can sit ONE cent away from
 * round(base_r × rate) — so the table would read 1,74 + 0,37 = 2,11 under a
 * TOTAL of 2,10. Nudge the table's IVA rows (≤ 1 cent per rate row) so their
 * sum equals the stored iva_total; anything larger is left alone (that is a
 * real drift for the audit script, not rounding). No-op when they already
 * agree — i.e. for every net-driven document. */
export const reconcileIvaRows = (byRate, storedIva) => {
  const rows = byRate || [];
  if (!rows.length || storedIva == null || storedIva === '') return rows;
  const target = round2(Number(storedIva) || 0);
  if (target === 0) return rows; // no stored breakdown (legacy/POS) → leave the table alone
  let diff = round2(target - rows.reduce((s, r) => s + round2(r.iva), 0));
  if (diff === 0 || Math.abs(diff) > 0.01 * rows.length + 1e-9) return rows;
  const out = rows.map((r) => ({ ...r }));
  // Give each cent to the row whose unrounded IVA is nearest the rounding edge
  // in the needed direction (largest fractional residual first).
  const step = diff > 0 ? 0.01 : -0.01;
  const order = out.map((r, i) => ({ i, frac: (r.iva * 100) - Math.floor(r.iva * 100) }))
    .sort((a, b) => (step > 0 ? b.frac - a.frac : a.frac - b.frac));
  for (let k = 0; k < order.length && Math.abs(diff) > 1e-9; k++) {
    out[order[k].i].iva = round2(round2(out[order[k].i].iva) + step);
    diff = round2(diff - step);
  }
  return out;
};
